import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { getComicById, getRelatedComics, getChaptersByComicId } from "@/mocks/comics";
import type { Comic, Chapter } from "@/mocks/comics";

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comicId = Number(id);
  const [comic, setComic] = useState<Comic | undefined>(undefined);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [related, setRelated] = useState<Comic[]>([]);
  const [chapterPage, setChapterPage] = useState(0);
  const [isReading, setIsReading] = useState(false);
  const [currentChapter, setCurrentChapter] = useState<Chapter | null>(null);

  const CHAPTERS_PER_PAGE = 20;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const found = getComicById(comicId);
    if (found) {
      setComic(found);
      setChapters(getChaptersByComicId(comicId));
      setRelated(getRelatedComics(found));
    } else {
      setComic(undefined);
      setChapters([]);
      setRelated([]);
    }
    setChapterPage(0);
    setIsReading(false);
    setCurrentChapter(null);
  }, [comicId]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
            <h2 className="mt-4 text-xl font-bold text-foreground-800">漫画未找到</h2>
            <p className="mt-2 text-sm text-foreground-500">该漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-block mt-4 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-label hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const totalChapterPages = Math.ceil(chapters.length / CHAPTERS_PER_PAGE);
  const paginatedChapters = chapters.slice(
    chapterPage * CHAPTERS_PER_PAGE,
    (chapterPage + 1) * CHAPTERS_PER_PAGE
  );

  const handleStartReading = (chapter?: Chapter) => {
    const target = chapter || chapters[chapters.length - 1];
    if (target) {
      setCurrentChapter(target);
      setIsReading(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://hanmanwu.com/" },
      { "@type": "ListItem", position: 2, name: "漫画分类", item: "https://hanmanwu.com/category/all" },
      { "@type": "ListItem", position: 3, name: comic.tags[0], item: `https://hanmanwu.com/category/${comic.category}` },
      { "@type": "ListItem", position: 4, name: comic.title, item: `https://hanmanwu.com/comic/${comic.id}` },
    ],
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${comic.title} - 韩漫网站免费在线阅读`,
    description: comic.description,
    author: { "@type": "Person", name: comic.author },
    datePublished: comic.updatedAt,
    dateModified: comic.updatedAt,
    url: `https://hanmanwu.com/comic/${comic.id}`,
  };

  return (
    <>
      <JsonLd data={breadcrumbSchema} />
      <JsonLd data={articleSchema} />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 py-4 text-xs text-foreground-500 font-label flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/category/all" className="hover:text-primary-600 transition-colors cursor-pointer">漫画分类</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to={`/category/${comic.category}`}
              className="hover:text-primary-600 transition-colors cursor-pointer"
            >
              {comic.tags[0]}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{comic.title}</span>
          </nav>

          <div className="mb-10">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="w-full md:w-56 flex-shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover"
                    title={`${comic.title} - 韩漫网站免费在线阅读`}
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  {comic.title}
                </h1>
                <div className="flex items-center gap-3 mt-3 flex-wrap">
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-xs font-label ${
                      comic.status === "连载"
                        ? "bg-accent-500 text-background-50"
                        : "bg-foreground-600 text-background-50"
                    }`}
                  >
                    {comic.status}
                  </span>
                  <span className="text-sm text-foreground-500 font-label">
                    作者：{comic.author}
                  </span>
                  <span className="text-sm text-accent-600 font-label">
                    <i className="ri-star-fill mr-0.5"></i>
                    {comic.rating}
                  </span>
                  <span className="text-sm text-foreground-400 font-label">
                    <i className="ri-eye-line mr-1"></i>
                    {(comic.views / 10000).toFixed(0)}万 人气
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mt-4">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-label"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="mt-5 text-sm md:text-base text-foreground-700 leading-relaxed">
                  {comic.description}
                </p>

                <div className="flex items-center gap-3 mt-6 flex-wrap">
                  <button
                    onClick={() => handleStartReading()}
                    className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-label transition-colors whitespace-nowrap cursor-pointer"
                  >
                    开始阅读 第一话
                  </button>
                  <button
                    onClick={() => {
                      const latest = chapters[0];
                      if (latest) handleStartReading(latest);
                    }}
                    className="px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 rounded-full text-sm font-label transition-colors whitespace-nowrap cursor-pointer border border-background-200/70"
                  >
                    最新话：第{chapters[0]?.number || "?"}话
                  </button>
                </div>
              </div>
            </div>
          </div>

          {isReading && currentChapter && (
            <div className="mb-10 p-4 md:p-6 bg-background-100 rounded-lg border border-background-200/70">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold font-heading text-foreground-950">
                  正在阅读：{currentChapter.title}
                </h3>
                <button
                  onClick={() => setIsReading(false)}
                  className="px-4 py-1.5 text-sm text-foreground-600 hover:text-foreground-800 font-label transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-close-line mr-1"></i>关闭阅读
                </button>
              </div>
              <div className="aspect-[3/4] max-w-md mx-auto bg-background-200 rounded-lg flex items-center justify-center">
                <div className="text-center p-8">
                  <i className="ri-book-open-line text-4xl text-foreground-400"></i>
                  <p className="mt-3 text-sm text-foreground-600 font-label">
                    {currentChapter.title} - 漫画内容区域
                  </p>
                  <p className="text-xs text-foreground-400 mt-1">
                    下拉式阅读 · 无删减完整内容
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 max-w-md mx-auto">
                <button
                  className="px-4 py-2 bg-background-50 hover:bg-background-200 text-foreground-700 rounded-full text-sm font-label transition-colors whitespace-nowrap cursor-pointer border border-background-200/70"
                  disabled={currentChapter.number >= chapters.length}
                >
                  <i className="ri-arrow-left-line mr-1"></i>上一话
                </button>
                <span className="text-xs text-foreground-500 font-label">
                  {currentChapter.number} / {chapters.length}
                </span>
                <button
                  className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-label transition-colors whitespace-nowrap cursor-pointer"
                  disabled={currentChapter.number <= 1}
                >
                  下一话<i className="ri-arrow-right-line ml-1"></i>
                </button>
              </div>
            </div>
          )}

          <div className="mb-10">
            <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-950 mb-4">
              章节列表
              <span className="text-sm text-foreground-500 font-label ml-2">
                共 {chapters.length} 话
              </span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {paginatedChapters.map((ch) => (
                <button
                  key={ch.id}
                  onClick={() => handleStartReading(ch)}
                  className={`px-3 py-2.5 rounded-md text-sm text-left transition-colors whitespace-nowrap cursor-pointer font-label ${
                    currentChapter?.id === ch.id
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-primary-50 border border-background-200/70"
                  }`}
                >
                  {ch.title}
                </button>
              ))}
            </div>
            {totalChapterPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-5">
                <button
                  onClick={() => setChapterPage(Math.max(0, chapterPage - 1))}
                  disabled={chapterPage === 0}
                  className="px-3 py-1.5 rounded-md text-sm bg-background-100 hover:bg-background-200 disabled:opacity-40 transition-colors cursor-pointer font-label"
                >
                  上一页
                </button>
                {Array.from({ length: totalChapterPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => setChapterPage(i)}
                    className={`w-8 h-8 rounded-md text-sm font-label transition-colors cursor-pointer ${
                      i === chapterPage
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() =>
                    setChapterPage(Math.min(totalChapterPages - 1, chapterPage + 1))
                  }
                  disabled={chapterPage >= totalChapterPages - 1}
                  className="px-3 py-1.5 rounded-md text-sm bg-background-100 hover:bg-background-200 disabled:opacity-40 transition-colors cursor-pointer font-label"
                >
                  下一页
                </button>
              </div>
            )}
          </div>

          {related.length > 0 && (
            <div className="mb-12">
              <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-950 mb-4">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/comic/${r.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-2">
                      <img
                        src={r.cover}
                        alt={r.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="text-xs font-medium font-heading text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {r.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}